"use client";

import React from "react";
import { createRoot } from "react-dom/client";
import {
  extractApiErrorMessage,
  type ApiErrorMessageOptions,
} from "@/utils/axiosError";

export type ToastType = "success" | "error" | "warning" | "info";
export enum ToastPosition {
  TOP_LEFT = "top-left",
  TOP_CENTER = "top-center",
  TOP_RIGHT = "top-right",
  MIDDLE_LEFT = "middle-left",
  MIDDLE_CENTER = "middle-center",
  MIDDLE_RIGHT = "middle-right",
  BOTTOM_LEFT = "bottom-left",
  BOTTOM_CENTER = "bottom-center",
  BOTTOM_RIGHT = "bottom-right",
}

interface ToastProps {
  message: string;
  type: ToastType;
  position?: ToastPosition;
  onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type,
  position = ToastPosition.TOP_RIGHT,
  onClose,
}) => {
  const toastClasses = {
    success: "bg-green-500/90",
    error: "bg-red-500/90",
    warning: "bg-yellow-500/90",
    info: "bg-blue-500/90",
  };

  const icon = {
    success: "✅",
    error: "❌",
    warning: "⚠️",
    info: "ℹ️",
  }[type];

  const getPositionClasses = (pos: ToastPosition) => {
    const baseClasses = "fixed z-100 flex items-center p-4 rounded-lg shadow-lg transition-all duration-300";
    
    switch (pos) {
      case "top-left":
        return `${baseClasses} top-4 left-4`;
      case "top-center":
        return `${baseClasses} top-4 left-1/2 transform -translate-x-1/2`;
      case "top-right":
        return `${baseClasses} top-4 right-4`;
      case "middle-left":
        return `${baseClasses} top-1/2 left-4 transform -translate-y-1/2`;
      case "middle-center":
        return `${baseClasses} top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`;
      case "middle-right":
        return `${baseClasses} top-1/2 right-4 transform -translate-y-1/2`;
      case "bottom-left":
        return `${baseClasses} bottom-4 left-4`;
      case "bottom-center":
        return `${baseClasses} bottom-4 left-1/2 transform -translate-x-1/2`;
      case "bottom-right":
        return `${baseClasses} bottom-4 right-4`;
      default:
        return `${baseClasses} top-4 right-4`;
    }
  };

  return (
    <div
      className={`${getPositionClasses(position)} ${toastClasses[type]}`}
    >
      <span className="text-white text-lg mr-3">{icon}</span>
      <p className="text-white text-sm font-medium whitespace-pre-line">
        {message}
      </p>
      <button
        type="button"
        className="absolute top-0 right-0 bg-transparent text-gray-400 hover:text-white hover:opacity-80 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 inline-flex items-center justify-center h-6 w-6 text-xs"
        onClick={onClose}
      >
        <span className="sr-only">Close</span>
        <svg
          className="w-2 h-2"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 14 14"
        >
          <path
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
          />
        </svg>
      </button>
    </div>
  );
};

// SSR-safe toast implementation
let toastContainer: HTMLElement | null = null;

const ensureContainer = () => {
  if (typeof window === 'undefined') return null; // SSR check
  
  if (!toastContainer) {
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.zIndex = "100";
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.gap = "1rem";
    container.style.width = "300px";

    document.body.appendChild(container);
    toastContainer = container;
  }
  
  return toastContainer;
};

export const Toaster = () => {
  React.useEffect(() => {
    if (typeof window === 'undefined') return; // SSR check
    
    ensureContainer();
    
    return () => {
      if (toastContainer && toastContainer.parentNode) {
        toastContainer.parentNode.removeChild(toastContainer);
        toastContainer = null;
      }
    };
  }, []);

  return null;
};

export const useToast = () => {
  const showToast = (message: string, type: ToastType, position?: ToastPosition) => {
    const container = ensureContainer();
    if (!container) return; // Skip during SSR
    
    let timeout = 3000;
    if(type === "info"){
      timeout = 10000;
    }

    const toastElement = document.createElement("div");
    container.appendChild(toastElement);

    const toast = React.createElement(Toast, {
      key: Date.now().toString(),
      message,
      type,
      position,
      onClose: () => {
        toastElement.remove();
      },
    });

    const root = createRoot(toastElement);
    root.render(toast);

    setTimeout(() => {
      root.unmount();
      toastElement.remove();
    }, timeout);
  };

  return {
    successMsg: (message: string, position?: ToastPosition) => showToast(message, "success", position),
    errorMsg: (
      error: unknown,
      position?: ToastPosition,
      options?: ApiErrorMessageOptions
    ) =>
      showToast(
        extractApiErrorMessage(
          error,
          options?.fallbackMessage || "Something went wrong. Please try again.",
          options
        ),
        "error",
        position
      ),
    warningMsg: (message: string, position?: ToastPosition) => showToast(message, "warning", position),
    infoMsg: (message: string, position?: ToastPosition) => showToast(message, "info", position),
  };
};
